import { useState } from "react";
import { FiImage, FiX, FiZoomIn } from "react-icons/fi";
import { MediaLightbox } from "./MediaLightbox";

interface ImagePreviewProps {
  /** Image URL */
  url: string;
  /** Filename for display */
  filename: string;
  /** Preview variant */
  variant: "inline" | "full";
  /** Optional className */
  className?: string | undefined;
}

/**
 * ImagePreview - Image thumbnail and full preview
 *
 * Inline: Small thumbnail for table rows
 * Full: Large preview with zoom option for modals
 */
export function ImagePreview({ url, filename, variant, className = "" }: ImagePreviewProps) {
  const [showLightbox, setShowLightbox] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (variant === "inline") {
    return (
      <>
        <div
          className={`flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity ${className}`}
          onClick={() => setShowLightbox(true)}
        >
          {imageError ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-100">
              <FiImage className="h-5 w-5 text-gray-400" />
            </div>
          ) : (
            <img
              src={url}
              alt={filename}
              className="h-10 w-10 shrink-0 rounded object-cover"
              onError={() => setImageError(true)}
              loading="lazy"
              decoding="async"
            />
          )}
          <p className="truncate text-sm font-medium text-gray-900">{filename}</p>
        </div>

        {/* Lightbox */}
        {showLightbox && (
          <MediaLightbox
            media={{ type: "IMAGE", url, filename }}
            onClose={() => setShowLightbox(false)}
          />
        )}
      </>
    );
  }

  // Full variant for modals
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="relative">
        {imageError ? (
          <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100">
            <div className="text-center">
              <FiImage className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">Failed to load image</p>
            </div>
          </div>
        ) : (
          <>
            <img
              src={url}
              alt={filename}
              className="max-h-96 w-full rounded-lg object-contain"
              onError={() => setImageError(true)}
              loading="lazy"
              decoding="async"
            />
            <button
              type="button"
              onClick={() => setShowLightbox(true)}
              className="absolute bottom-2 right-2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              aria-label="View full size"
            >
              <FiZoomIn className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <p className="text-sm text-gray-600">{filename}</p>

      {/* Lightbox */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setShowLightbox(false)}
        >
          <button
            type="button"
            onClick={() => setShowLightbox(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <FiX className="h-6 w-6" />
          </button>
          <img
            src={url}
            alt={filename}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default ImagePreview;
